import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Typography,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import { colors } from "../../../../theme";
import { zoneRiskLabel, zoneRiskStyle } from "./zoneUtils";

// Bulk "assign selected assets to a zone" dialog per la griglia Inventory --
// la creazione/modifica/eliminazione zona completa ora vive sul grafo
// Network Zones (ZoneFormDialog, aperto dal "+" per riga o dal pannello
// Inspector), non più qui: questo dialog resta solo per l'assegnazione
// massiva, l'unico caso d'uso di cui Network Zones non si occupa.
const ZoneManagerDialog = ({ open, onClose, zones, selectedCount, onAssign, onUnassign }) => {
  const [assignValue, setAssignValue] = useState("");

  const sortedZones = useMemo(
    () => [...zones].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [zones]
  );

  useEffect(() => {
    if (!open) setAssignValue("");
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#161616", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, borderBottom: `1px solid ${colors.grey[800]}`, pb: 1.5 }}>
        <PlaceIcon sx={{ color: "#459095", fontSize: 22 }} />
        <Box>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: colors.grey[100] }}>Assign to Zone</Typography>
          <Typography sx={{ fontSize: 12, color: colors.grey[400], mt: 0.2 }}>
            Assign the selected asset{selectedCount === 1 ? "" : "s"} to a zone.
          </Typography>
        </Box>
        <Chip
          label={`${zones.length} zone${zones.length === 1 ? "" : "s"}`}
          size="small"
          sx={{ ml: "auto", bgcolor: "#45909522", color: "#459095", fontSize: 11, fontWeight: 700 }}
        />
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: "#161616", pt: 3 }}>
        <Typography sx={{ fontSize: 11, color: colors.grey[500], mb: 1.2, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.8 }}>
          Assign to {selectedCount} selected asset{selectedCount === 1 ? "" : "s"}
        </Typography>
        {zones.length === 0 ? (
          <Typography sx={{ color: colors.grey[500], fontSize: 12.5, fontStyle: "italic" }}>
            No zones yet — create one from the Network Zones page first.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel>Zone</InputLabel>
              <Select label="Zone" value={assignValue} onChange={(e) => setAssignValue(e.target.value)}>
                {sortedZones.map((z) => {
                  const style = zoneRiskStyle(z.risk);
                  return (
                    <MenuItem
                      key={z._id ?? z.id}
                      value={z._id ?? z.id}
                      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}
                    >
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: colors.grey[100] }}>
                        {z.name}
                      </Typography>
                      <Chip
                        label={zoneRiskLabel(z.risk)}
                        size="small"
                        sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: 10, height: 19, flexShrink: 0 }}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              disableElevation
              disabled={!assignValue}
              onClick={() => { onAssign(assignValue); setAssignValue(""); }}
            >
              Assign
            </Button>
            <Button onClick={onUnassign}>Clear Zone</Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#161616", borderTop: `1px solid ${colors.grey[800]}` }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ZoneManagerDialog;
